/**
 * @file See-through holes in walls that stand between the camera and a body.
 *
 * The top-down rig is fixed (GDD §4), so a wall one or two tiles toward the
 * camera is tall enough to swallow whatever stands behind it — the player
 * included. This opens a soft circle in exactly those walls, and only where
 * they actually cover someone.
 *
 * The test is a cone from the eye through each character: a wall fragment
 * hides if it sits inside that cone *and* between the eye and the character.
 * Framing it that way is what keeps this cheap and exact — it needs no
 * projection matrices, no screen-space pass and no camera bookkeeping on the
 * CPU, because `cameraPosition` is a built-in uniform that always belongs to
 * whichever camera is drawing. A system feeds world-space points in; nothing
 * here knows about the ECS.
 *
 * The fade is dithered rather than blended: `discard` keeps the walls opaque,
 * so they stay in one draw call and keep writing depth like solid geometry,
 * which alpha blending would cost us.
 */

import * as THREE from 'three';

/**
 * Interleaved gradient noise — the dither threshold. Screen-locked and stable
 * frame to frame, which is what stops the rim from boiling as the camera pans.
 */
const DITHER_GLSL = /* glsl */ `
float wallCutDither(vec2 p) {
  return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715))));
}
`;

export class WallCutout {
  /**
   * @param {THREE.Material} base Material to clone; the walls keep their look.
   * @param {import('../config/GameConfig.js').GameConfig['wallCutout']} settings
   *   Tunables. `maxCharacters` is baked into the shader, so it is read once.
   */
  constructor(base, settings) {
    const slots = Math.max(1, Math.round(settings?.maxCharacters ?? 8));

    /** @type {number} Shader-side slot count. @private */
    this._slots = slots;
    /** @type {number} Next slot {@link WallCutout#add} will write. @private */
    this._cursor = 0;

    /**
     * xyz is a world-space point, w flags the slot as live. Dead slots stay in
     * the array rather than shortening a loop: a constant trip count is what
     * lets the shader compiler unroll it.
     * @type {THREE.Vector4[]}
     * @private
     */
    this._centers = Array.from({ length: slots }, () => new THREE.Vector4(0, 0, 0, 0));

    /** @type {{ uCutCenters: { value: THREE.Vector4[] }, uCutRadius: { value: number }, uCutFeather: { value: number } }} @private */
    this._uniforms = {
      uCutCenters: { value: this._centers },
      uCutRadius: { value: settings?.radius ?? 1.7 },
      // Guarded below 1: a feather of exactly 1 collapses the smoothstep the
      // rim is made of, which would turn the soft edge into a hard cookie cut.
      uCutFeather: { value: Math.min(0.99, Math.max(0, settings?.feather ?? 0.35)) }
    };

    /** @type {THREE.Material} @private */
    this._material = /** @type {THREE.Material} */ (base.clone());
    this._install(this._material, slots);
  }

  /** @returns {THREE.Material} The wall material, cutout and all. */
  get material() {
    return this._material;
  }

  /**
   * Opens a frame of writing. Every slot is rewritten or retired between this
   * and {@link WallCutout#end}, so a character that stopped being relevant can
   * never leave a hole behind.
   * @returns {void}
   */
  begin() {
    this._cursor = 0;
  }

  /**
   * Adds a point to open the walls around. Silently ignores anything past the
   * slot budget, which makes the caller's ranking the only thing that decides
   * who gets a hole.
   *
   * @param {number} x World X.
   * @param {number} y World Y; aim at the middle of the body, not its feet.
   * @param {number} z World Z.
   * @returns {void}
   */
  add(x, y, z) {
    if (this._cursor >= this._slots) return;
    this._centers[this._cursor++].set(x, y, z, 1);
  }

  /**
   * Retires the slots this frame did not claim.
   * @returns {void}
   */
  end() {
    for (let i = this._cursor; i < this._slots; i++) this._centers[i].w = 0;
  }

  /**
   * Closes every hole — what the caller wants when the feature is switched off
   * or there is nobody left to keep visible.
   * @returns {void}
   */
  clear() {
    this.begin();
    this.end();
  }

  /**
   * Re-reads the tunables that are not baked into the shader, so the debug
   * tuning layer can move them at runtime.
   *
   * @param {import('../config/GameConfig.js').GameConfig['wallCutout']} settings Tunables.
   * @returns {void}
   */
  applySettings(settings) {
    if (!settings) return;
    this._uniforms.uCutRadius.value = settings.radius ?? this._uniforms.uCutRadius.value;
    this._uniforms.uCutFeather.value = Math.min(0.99, Math.max(0, settings.feather ?? 0.35));
  }

  /** @returns {void} */
  dispose() {
    this._material.dispose();
  }

  /**
   * Injects the cutout into a cloned standard material.
   *
   * @param {THREE.Material} material Material to patch in place.
   * @param {number} slots Baked slot count.
   * @returns {void}
   * @private
   */
  _install(material, slots) {
    const uniforms = this._uniforms;

    // Two standard materials with identical parameters share a compiled program
    // unless their cache keys differ — without this the walls would silently
    // borrow the plain stone program (or lend it theirs) and the cutout would
    // land on the floors instead.
    material.customProgramCacheKey = () => `wall-cutout:${slots}`;

    material.onBeforeCompile = (shader) => {
      shader.uniforms.uCutCenters = uniforms.uCutCenters;
      shader.uniforms.uCutRadius = uniforms.uCutRadius;
      shader.uniforms.uCutFeather = uniforms.uCutFeather;

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
           varying vec3 vCutWorldPos;`
        )
        // After project_vertex, `transformed` is final: morphs, skinning and
        // the rise animation have all had their say.
        .replace(
          '#include <project_vertex>',
          `#include <project_vertex>
           vec4 cutWorld = vec4(transformed, 1.0);
           #ifdef USE_INSTANCING
             cutWorld = instanceMatrix * cutWorld;
           #endif
           vCutWorldPos = (modelMatrix * cutWorld).xyz;`
        );

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
           #define WALL_CUT_SLOTS ${slots}
           uniform vec4 uCutCenters[WALL_CUT_SLOTS];
           uniform float uCutRadius;
           uniform float uCutFeather;
           varying vec3 vCutWorldPos;
           ${DITHER_GLSL}`
        )
        // First thing in main(), so a discarded fragment costs nothing past
        // this point — no texture fetches, no lighting.
        .replace(
          '#include <clipping_planes_fragment>',
          `#include <clipping_planes_fragment>
           {
             float cutHide = 0.0;
             vec3 cutToFrag = vCutWorldPos - cameraPosition;
             for (int i = 0; i < WALL_CUT_SLOTS; i++) {
               vec4 cutCenter = uCutCenters[i];
               if (cutCenter.w < 0.5) continue;

               vec3 cutToChar = cutCenter.xyz - cameraPosition;
               float cutCharDist = length(cutToChar);
               if (cutCharDist < 1e-4) continue;

               vec3 cutAxis = cutToChar / cutCharDist;
               float cutAlong = dot(cutToFrag, cutAxis);
               // Only what is genuinely between the eye and the body may open:
               // a wall behind the character is not in the way.
               if (cutAlong <= 0.0 || cutAlong >= cutCharDist) continue;

               float cutPerp = length(cutToFrag - cutAxis * cutAlong);
               // A cone rather than a cylinder, so the hole reads as one size
               // on screen no matter how far up the wall the fragment sits.
               float cutRadius = uCutRadius * (cutAlong / cutCharDist);
               cutHide = max(cutHide, 1.0 - smoothstep(cutRadius * uCutFeather, cutRadius, cutPerp));
             }
             if (cutHide > 0.0 && cutHide > wallCutDither(gl_FragCoord.xy)) discard;
           }`
        );
    };

    material.needsUpdate = true;
  }
}
